import '.hx{z-index:10;}.ssr-plugin'
import '.ak{position:fixed;}.ssr-plugin'
import '.a1{pointer-events:none;}.ssr-plugin'
import '.hy{inset:24px;}.ssr-plugin'
import '.u{position:absolute;}.ssr-plugin'
import '.dd{right:0px;}.ssr-plugin'
import '.a4{bottom:0px;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.hz{justify-content:end;}.ssr-plugin'
import '.i0{transition:all 250ms;}.ssr-plugin'
import '.i1{pointer-events:all;}.ssr-plugin'
import '.eh{display:inline-flex;}.ssr-plugin'
import '.i2{width:400px;}.ssr-plugin'
import '.i{flex-direction:column;}.ssr-plugin'
import '.ap{gap:12px;}.ssr-plugin'
import '.eb{flex-shrink:0;}.ssr-plugin'
import '.a5{padding:16px;}.ssr-plugin'
import '.d{align-items:center;}.ssr-plugin'
import '.d2{gap:8px;}.ssr-plugin'
import '.a8{gap:4px;}.ssr-plugin'
import '.c{justify-content:center;}.ssr-plugin'
import '.bv{margin-left:auto;}.ssr-plugin'
import '.am{overflow:hidden;}.ssr-plugin'
import '.i3{max-height:154px;}.ssr-plugin'
import '.ck{overflow:auto;}.ssr-plugin'
import '.i4{margin-left:-16px;}.ssr-plugin'
import '.i5{margin-right:-16px;}.ssr-plugin'
import '.i6{margin-bottom:-16px;}.ssr-plugin'
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  forwardRef,
  useState,
  useEffect,
  useRef,
} from 'react'
import { Icon, IconProps } from '../Icon/index.js'
import { Button } from '../Button/index.js'
import { IconButton } from '../IconButton/index.js'
import { radius } from '../../utils/radius.js'
import { colors, Color } from '../../tokens/colors.js'
import { Text } from '../Text/index.js'
import { styled } from 'inlines'
import { shadows } from '../../utils/shadows.js'

type ToastFn = {
  (
    message: string,
    options?: Omit<
      BaseToastProps &
        (
          | { icon?: IconProps['variant']; leadElement?: never }
          | { icon?: never; leadElement?: ReactNode }
        ),
      'message' | 'onDismiss'
    >,
  ): string
  dismiss: (id: string) => void
}

type ToastContextProps = ToastFn

const ToastContext = createContext<ToastContextProps>(
  Object.assign(() => '', { dismiss: () => {} }),
)

type ToastProviderProps = {
  children: ReactNode
}

function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<
    (ToastProps & {
      id: string
      visibility: 'entering' | 'visible' | 'leaving'
      height: number
    })[]
  >([])

  const timeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map())

  const clearToastTimeouts = useCallback((id: string) => {
    const timeouts = timeoutsRef.current.get(id)
    if (timeouts) {
      clearTimeout(timeouts)
      timeoutsRef.current.delete(id)
    }
  }, [])

  const dismiss = useCallback(
    (id: string) => {
      clearToastTimeouts(id)
      setToasts((p) =>
        p.map((e) => (e.id === id ? { ...e, visibility: 'leaving' } : e)),
      )
      const timeout = setTimeout(() => {
        setToasts((p) => p.filter((e) => e.id !== id))
        timeoutsRef.current.delete(id)
      }, 250)
      timeoutsRef.current.set(`${id}-leaving`, timeout)
    },
    [clearToastTimeouts],
  )

  const toast = useCallback(
    (message: string, options?: Omit<ToastProps, 'message' | 'onDismiss'>) => {
      const id = crypto.randomUUID()
      setToasts((p) => [
        ...p,
        {
          id,
          message,
          visibility: 'entering',
          height: 0,
          ...options,
        } as ToastProps & {
          id: string
          visibility: 'entering' | 'visible' | 'leaving'
          height: number
        },
      ])

      const enterTimeout = setTimeout(() => {
        setToasts((p) =>
          p.map((e) => (e.id === id ? { ...e, visibility: 'visible' } : e)),
        )
      }, 250)
      timeoutsRef.current.set(`${id}-enter`, enterTimeout)

      if (
        (options?.primaryButtonLabel &&
          options?.secondaryButtonLabel &&
          options?.permanent !== false) ||
        options?.permanent
      )
        return id

      const dismissTimeout = setTimeout(() => {
        dismiss(id)
      }, 5000)
      timeoutsRef.current.set(id, dismissTimeout)

      return id
    },
    [dismiss],
  ) as ToastFn

  toast.dismiss = dismiss

  useEffect(() => {
    return () => {
      for (const timeout of timeoutsRef.current.values()) {
        clearTimeout(timeout)
      }
      timeoutsRef.current.clear()
    }
  }, [])

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {!!toasts.length && (
        <div className="hx ak a1 hy"
        >
          {toasts.map((toast, index) => (
            <div className="u dd a4 a hz i0"
              key={toast.id}
              ref={(el) => {
                if (el && toasts.find((e) => e.id === toast.id).height === 0) {
                  setToasts((p) =>
                    p.map((e) =>
                      e.id === toast.id
                        ? { ...e, height: el.getBoundingClientRect().height }
                        : e,
                    ),
                  )
                }
              }}
              style={{
                opacity:
                  toast.visibility === 'visible' && index > toasts.length - 6
                    ? 1
                    : 0,
                transform: (() => {
                  if (toast.visibility === 'entering') {
                    return `translateY(100%)`
                  }

                  let heightOfToastsBelow = 0

                  for (let i = index; i < toasts.length - 1; i++) {
                    heightOfToastsBelow += toasts[i + 1].height + 8
                  }

                  if (toast.visibility === 'leaving') {
                    return `translateY(-${heightOfToastsBelow}px) translateX(120px)`
                  }

                  return `translateY(-${heightOfToastsBelow}px)`
                })(),
              }}
            >
              <Toast
                {...toast}
                onDismiss={() => {
                  dismiss(toast.id)
                }}
              />
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  )
}

function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return context
}

type BaseToastProps = {
  message: string
  description?: string
  iconColor?: Color
  onPrimaryClick?: () => void
  onSecondaryClick?: () => void
  onDismiss?: () => void
  primaryButtonLabel?: string
  secondaryButtonLabel?: string
  color?: 'red' | 'neutral'
  expandedContent?: ReactNode
  permanent?: boolean
  ref?: React.MutableRefObject<HTMLDivElement>
}

type ToastProps = BaseToastProps &
  (
    | { icon?: IconProps['variant']; leadElement?: never }
    | { icon?: never; leadElement?: ReactNode }
  )

const Toast = forwardRef<HTMLDivElement, ToastProps>(
  (
    {
      message,
      description,
      icon,
      leadElement,
      iconColor,
      primaryButtonLabel,
      onPrimaryClick,
      secondaryButtonLabel,
      onSecondaryClick,
      onDismiss,
      color = 'neutral',
      expandedContent,
    },
    ref,
  ) => {
    const [expanded, setExpanded] = useState(true)

    if (
      (onPrimaryClick && !primaryButtonLabel) ||
      (!onPrimaryClick && primaryButtonLabel)
    ) {
      throw new Error(
        'Toast: need to provide both onClick and label for primary button',
      )
    }
    if (
      (onSecondaryClick && !secondaryButtonLabel) ||
      (!onSecondaryClick && secondaryButtonLabel)
    ) {
      throw new Error(
        'Toast: need to provide both onClick and label for secondary button',
      )
    }

    return (
      <styled.div className="i1 eh i2 i ap eb a5"
        style={{
          borderRadius: radius[20],
          boxShadow: shadows.popoverLarge,
          ...(color === 'neutral' && {
            border: `1px solid ${colors.neutral20Adjusted}`,
            background: colors.neutralInverted100,
          }),
          ...(color === 'red' && {
            background: colors.red100,
            border: `1px solid ${colors.white20}`,
          }),
        }}
        ref={ref}
      >
        <div className="a d ap">
          <styled.div className="a d d2"
            style={{
              color: color === 'red' ? colors.white100 : colors.neutral80,
              '& svg': {
                color: colors[iconColor],
              },
            }}
          >
            {(icon || leadElement) &&
              (icon ? <Icon variant={icon} /> : leadElement)}
            <div className="a i a8"
            >
              <Text
                variant="display-medium"
                color={color === 'red' ? 'white100' : 'neutral80'}
              >
                {message}
              </Text>
              {description && (
                <Text
                  variant="display-regular"
                  color={color === 'red' ? 'white80' : 'neutral60'}
                >
                  {description}
                </Text>
              )}
            </div>
          </styled.div>

          <div className="a c d bv ap"
          >
            {((primaryButtonLabel && !secondaryButtonLabel) ||
              (!primaryButtonLabel && secondaryButtonLabel)) && (
              <>
                {primaryButtonLabel && (
                  <Button
                    onClick={() => {
                      onPrimaryClick()
                      onDismiss?.()
                    }}
                  >
                    {primaryButtonLabel}
                  </Button>
                )}
                {secondaryButtonLabel && (
                  <Button
                    variant="border"
                    onClick={() => {
                      onSecondaryClick()
                      onDismiss?.()
                    }}
                  >
                    {secondaryButtonLabel}
                  </Button>
                )}
              </>
            )}
            {expandedContent && (
              <IconButton
                onClick={() => setExpanded(!expanded)}
                icon={!expanded ? 'chevron-down' : 'chevron-up'}
                color={color}
              />
            )}

            <div className="am"
              style={{
                width: expandedContent ? 1 : 36,
                height: expandedContent ? 1 : 36,}}
            >
              <IconButton
                onClick={onDismiss}
                icon="close"
                color={color}
                toggled={color === 'red'}
              />
            </div>
          </div>
        </div>
        {primaryButtonLabel && secondaryButtonLabel && (
          <div className="a d2 d"
          >
            {icon && <div style={{ height: 24, width: 24 }} />}
            <Button
              onClick={() => {
                onPrimaryClick()
                onDismiss?.()
              }}
            >
              {primaryButtonLabel}
            </Button>
            <Button
              variant="border"
              onClick={() => {
                onSecondaryClick()
                onDismiss?.()
              }}
            >
              {secondaryButtonLabel}
            </Button>
          </div>
        )}

        {expandedContent && (
          <div className="i3 ck i4 i5 i6"
            style={{
              height: expanded ? 'auto' : 0,
              padding: expanded ? 8 : 0,
              borderTop: expanded
                ? `1px solid ${colors.neutral20Adjusted}`
                : 'none',
            }}
          >
            {expandedContent}
          </div>
        )}
      </styled.div>
    )
  },
)

export { ToastProvider, useToast }
export type { ToastProviderProps }
